function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

import React, { useContext } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { white, grey5, spacing32 } from "@churchofjesuschrist/eden-style-constants";
import { StyledTHead } from "./THead";
import { StyledTR } from "./TR";
import { cell, cellContents } from "./cell";
import { useColumnSortingContext } from "./columnSorting/context";
import { Context as SettingsContext } from "./settings/context";
import { Context as OverflowContext } from "./overflow/context";
import hexToRgba from "hex2rgba";
const shadowColor = hexToRgba(grey5, "0.5");
/*
Some weirdness when dealing with the sticky header:

1. Since we can only "stick" <th> elements (and not the <thead> or <tr>s), we
   have to also apply the white background color to the <th>. Normally, we
   would just use `background-color:white` on the `... th` selector, However,
   when doing so in FF and Edge, any vertical borders disappear (if they are
   otherwise present). Instead, the `&::after` ruleset handles the white
   background. Gripes.
  
2. We are only making that last row's <th>s stick.

3. For some reason when there are multiple rows in the header and
   `stickyHeader` is present, there is a 1px offset problem at the top. The
   `tr:not(:only-of-type) th` ruleset fixes this.

4. In Edge, the `& > *` ruleset is needed instead of just assigning
   a `z-index:-1` to the `&::after` ruleset. This also requires wrapping the
   `Normal` components children in an element. Gripes.

5. Currently there is not an easy way to turn on the shadows when a
   `stickyHeader` is present AND an <OverflowWrapper> is NOT used. For now, we
   are only using the `hasTop` value provided by the `OverflowContext` as the
   deciding factor on whether a TH is "stuck." 

NOTE: In `ColumnSortingCell` there are some :hover state overrides.

Also, the `StyledTH` only contains the `cell` shared CSS because it needs to be
reused in a couple different components which each handle applying the
`cellContents` shared CSS differently.
*/

const StyledTH = styled.th.withConfig({
  componentId: "sc-arbpvo-0"
})(["", " ", " ", ":last-of-type &&{position:sticky;", " & > *{position:relative;z-index:1;}&::after{content:\"\";position:absolute;top:0px;left:0px;bottom:3px;right:0px;background-color:", ";}}", ""], cell,
/* sc-selector */
StyledTHead, StyledTR, ({
  stickyHeader,
  stuck
}) => stickyHeader ? `top: 0px; ${stuck ? `&::before {
      content: "";
      pointer-events: none;
      position: absolute;
      top: 100%;
      left: 0;
      right: 0;
      height: ${spacing32};
      background-image: linear-gradient(to top, rgba(0,0,0,0), ${shadowColor});
    }` : ""}` : "", ({
  stickyHeader
}) => stickyHeader ? white : "transparent", ({
  stickyHeader
}) => stickyHeader ? `${
/* sc-selector */
StyledTHead} ${StyledTR}:not(:only-of-type) && {
        top: -1px;
      }` : "");
/*
Edge currently requires us to wrap `Normal`'s children in a span in order to 
apply the CSS explained above. Otherwise we could replace the next two things
with the following:

  const Normal = styled(StyledTH)`
    ${cellContents}
  `;
*/

const StyledStyledTH = styled(StyledTH).withConfig({
  componentId: "sc-arbpvo-1"
})(["", ""], cellContents); // eslint-disable-next-line react/prop-types

const Normal = ({
  children,
  ...props
}) => /*#__PURE__*/React.createElement(StyledStyledTH, props, /*#__PURE__*/React.createElement("span", null, children)); // Renders either a `Normal` or `ColumnSortingCell` th depending on whether
// column sorting is being used in the cell.


const TH = ({
  type = "normal",
  ...props
}) => {
  const {
    hasTop
  } = useContext(OverflowContext);
  const {
    spacing,
    verticalRuled,
    horizontalRuled,
    stickyHeader
  } = useContext(SettingsContext);
  const {
    ColumnSortingCell
  } = useColumnSortingContext();
  const Cell = props.sortColumn && ColumnSortingCell ? ColumnSortingCell : Normal;
  return /*#__PURE__*/React.createElement(Cell, _extends({}, props, {
    spacing: spacing,
    verticalRuled: verticalRuled,
    horizontalRuled: horizontalRuled,
    stickyHeader: stickyHeader,
    stuck: hasTop,
    dataType: type
  }));
};

export { StyledTH };
export default TH;
TH.propTypes = {
  /** The data type. */
  type: PropTypes.oneOf(["normal", "number", "actions"]),

  /** The column key for when the `Table` has `columnSorting`. */
  sortColumn: PropTypes.string,

  /** The cell contents. */
  children: PropTypes.node
};